import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GraduationCap, Layers, Users, CalendarDays, Clock, ArrowRight, User, MapPin, CalendarCheck, History, CalendarClock, ClipboardCheck } from "lucide-react";
import { Layout } from "../components/layout/Layout";
import { MonthCalendar } from "../components/planning/MonthCalendar";
import { SessionDialog } from "../components/planning/SessionDialogs";
import { Stat, StatusChip, EmptyState, ProgressBar, Panel } from "../components/common";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { api } from "../lib/api";
import { useAuth, hasPerm, isEleve, moduleVisible } from "../context/AuthContext";
import { monthGrid, todayISO, fmtDate, fmtDateLong, parseISODate, JOURS, MOIS } from "../lib/format";

function SessionRow({ s, onClick, showDate = true }) {
  const d = parseISODate(s.date);
  return (
    <li>
      <button onClick={() => onClick(s)} className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-accent/60 transition-colors" data-testid={`dash-session-${s.id}`}>
        {showDate && (
          <div className="w-11 shrink-0 rounded-lg border border-border text-center py-1">
            <div className="text-[10px] uppercase text-muted-foreground leading-tight">{JOURS[d.getDay()].slice(0, 3)}</div>
            <div className="text-lg font-semibold leading-tight tabular-nums">{d.getDate()}</div>
            <div className="text-[10px] text-muted-foreground leading-tight">{MOIS[d.getMonth()].slice(0, 4)}.</div>
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 justify-between">
            <p className="text-sm font-semibold truncate">{s.module_nom}</p>
            <StatusChip statut={s.statut} />
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
            <span className="flex items-center gap-1 tabular-nums"><Clock size={12} />{s.heure_debut} – {s.heure_fin}</span>
            {s.formateur_nom && <span className="flex items-center gap-1"><User size={12} />{s.formateur_nom}</span>}
            {s.salle && <span className="flex items-center gap-1"><MapPin size={12} />{s.salle}</span>}
            <span className="flex items-center gap-1"><Users size={12} />{s.eleves?.length || 0}</span>
          </p>
        </div>
      </button>
    </li>
  );
}

function SessionList({ list, onClick, empty, showDate }) {
  if (!list.length) return <EmptyState icon={CalendarDays} title={empty} />;
  return <ul className="divide-y divide-border">{list.map((s) => <SessionRow key={s.id} s={s} onClick={onClick} showDate={showDate} />)}</ul>;
}

export default function Dashboard() {
  const { user, modules: visibleModules } = useAuth();
  const navigate = useNavigate();
  const eleve = isEleve(user);
  const canManage = hasPerm(user, "planning", "manage");
  const now = new Date();
  const [ym, setYm] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [selected, setSelected] = useState(todayISO());
  const [summary, setSummary] = useState(null);
  const [monthSessions, setMonthSessions] = useState([]);
  const [current, setCurrent] = useState(null);
  const [mods, setMods] = useState([]);
  const [eleves, setEleves] = useState([]);
  const [tab, setTab] = useState("jour");

  const range = useMemo(() => {
    const g = monthGrid(ym.y, ym.m);
    return { start: g[0].iso, end: g[g.length - 1].iso };
  }, [ym]);

  const loadSummary = useCallback(() => api.get("/dashboard").then(({ data }) => setSummary(data)).catch(() => {}), []);
  const loadMonth = useCallback(() => api.get("/planning/sessions", { params: range }).then(({ data }) => setMonthSessions(data)).catch(() => setMonthSessions([])), [range]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadMonth(); }, [loadMonth]);
  useEffect(() => {
    if (!canManage) return;
    api.get("/modules").then(({ data }) => setMods(data)).catch(() => {});
    api.get("/eleves").then(({ data }) => setEleves(data)).catch(() => {});
  }, [canManage]);

  const daySessions = monthSessions.filter((s) => s.date === selected);
  const selectDay = (iso) => { setSelected(iso); setTab("jour"); };
  const showPlanning = !eleve && moduleVisible(visibleModules, "planning") && hasPerm(user, "planning", "view");

  return (
    <Layout title={eleve ? `Bonjour ${user.prenom}` : "Tableau de bord"}>
      <div className="space-y-5">
        {eleve ? (
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-card p-4 sm:col-span-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Ma progression</p>
                <Link to="/mon-suivi" className="text-xs font-medium text-primary hover:underline flex items-center gap-1">Mon suivi <ArrowRight size={12} /></Link>
              </div>
              <p className="mt-2 font-heading text-3xl font-semibold tabular-nums">{summary?.progress?.percent ?? 0} %</p>
              <ProgressBar percent={summary?.progress?.percent ?? 0} className="mt-2" />
              <p className="text-xs text-muted-foreground mt-2">
                {summary?.progress ? `${summary.progress.done}/${summary.progress.total} activités réalisées · ${summary.progress.modules_termines}/${summary.progress.modules} modules terminés` : "—"}
              </p>
            </div>
            <Stat label="Cours cette semaine" value={summary?.cours_semaine ?? "—"} icon={CalendarCheck} hint={user.access_end ? `Accès jusqu'au ${fmtDate(user.access_end)}` : undefined} />
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <Stat label="Élèves" value={summary?.stats?.eleves ?? "—"} icon={GraduationCap} />
            <Stat label="Modules" value={summary?.stats?.modules ?? "—"} icon={Layers} />
            <Stat label="Formateurs" value={summary?.stats?.formateurs ?? "—"} icon={Users} />
            <Stat label="Cours cette semaine" value={summary?.stats?.cours_semaine ?? "—"} icon={CalendarCheck} />
          </div>
        )}

        <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
          <MonthCalendar year={ym.y} month={ym.m} onMonthChange={(y, m) => setYm({ y, m })} sessions={monthSessions} selected={selected} onSelect={selectDay} />

          <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col">
            <Tabs value={tab} onValueChange={setTab} className="flex flex-col flex-1">
              <div className="px-3 pt-3">
                <TabsList className="w-full grid grid-cols-3">
                  <TabsTrigger value="jour" className="gap-1 text-xs sm:text-sm"><CalendarDays size={14} /> Jour</TabsTrigger>
                  <TabsTrigger value="avenir" className="gap-1 text-xs sm:text-sm" data-testid="dash-tab-avenir"><CalendarClock size={14} /> À venir</TabsTrigger>
                  <TabsTrigger value="passes" className="gap-1 text-xs sm:text-sm" data-testid="dash-tab-passes"><History size={14} /> Passés</TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="jour" className="mt-0 flex-1">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
                  <p className="font-heading font-semibold capitalize text-sm">{selected === todayISO() ? "Aujourd'hui · " : ""}{fmtDateLong(selected)}</p>
                  {showPlanning && (
                    <Button variant="ghost" size="sm" onClick={() => navigate(`/planning?date=${selected}`)} data-testid="dash-open-planning">Planning <ArrowRight size={14} /></Button>
                  )}
                </div>
                <SessionList list={daySessions} onClick={setCurrent} empty="Aucun cours ce jour" showDate={false} />
              </TabsContent>
              <TabsContent value="avenir" className="mt-0">
                <SessionList list={summary?.a_venir || []} onClick={setCurrent} empty="Aucun cours à venir" />
              </TabsContent>
              <TabsContent value="passes" className="mt-0">
                <SessionList list={summary?.passes || []} onClick={setCurrent} empty="Aucun cours passé" />
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {eleve && summary?.modules?.length > 0 && (
          <Panel title="Mes modules" icon={ClipboardCheck} actions={<Link to="/mon-suivi" className="text-xs font-medium text-primary hover:underline">Voir le détail</Link>}>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {summary.modules.map((m) => (
                <div key={m.id} className="rounded-lg border border-border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{m.module_nom}</p>
                    <span className="text-xs tabular-nums text-muted-foreground">{m.progress.done}/{m.progress.total}</span>
                  </div>
                  <ProgressBar percent={m.progress.percent} className="mt-2" tone={m.termine ? "success" : "primary"} />
                </div>
              ))}
            </div>
          </Panel>
        )}
      </div>

      <SessionDialog
        open={!!current}
        onOpenChange={(o) => !o && setCurrent(null)}
        session={current}
        canManage={canManage}
        modules={mods}
        eleves={eleves}
        onChanged={async (u) => {
          await Promise.all([loadMonth(), loadSummary()]);
          if (!u) setCurrent(null);
          else { const { data } = await api.get(`/planning/sessions/${u.id}`); setCurrent(data); }
        }}
      />
    </Layout>
  );
}
